import 'bootstrap/dist/css/bootstrap.css';
import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import Spinner from '../../shared/spinner';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faSearch, faFilePdf, faFileExcel, faCheck,
    faExclamationTriangle, faEdit, faMoneyBillWave, faPlus, faTimes,
    faPrint, faTrash, faSync, faHistory, faFileCode, faCheckDouble, faUniversity, faEye
} from '@fortawesome/free-solid-svg-icons';
import { Button } from 'react-bootstrap';
import CustomAlert from '../../shared/customAlert';
import FaturaService from '../../services/faturaService';
import PropostaService from '../../services/propostaService';
import ComissaoService from '../../services/comissaoService';
import PromotoraService from '../../services/promotoraService';
import RegionalService from '../../services/regionalService';
import ComercialService from '../../services/comercialService';
import { FaturaDTO } from '../../shared/dto/faturaDTO';
import { format } from 'date-fns';

interface HistoricoFaturaDTO {
    id: number;
    dataEvento: string;
    descricao: string;
    usuario: string;
}

interface ContaBancariaDTO {
    id: number;
    nomeIdentificacao: string;
    bancoNome: string;
    agencia: string;
    conta: string;
    principal: boolean;
}

interface FaturaResponseDTO extends Omit<FaturaDTO, 'status'> {
    nomeParceiro?: string;
    dataCriacao?: string;
    totalComissoes?: number;
    totalEstornos?: number;
    valorLiquido?: number;
    itens?: any[];
    status: string;
    possuiRestricaoParceiro?: boolean;
    motivoRestricao?: string;
}

function FaturaGestaoComponent() {
    const [alert, setAlert] = useState<{ message: string, type: 'success' | 'warning' | 'error' } | null>(null);
    const [loading, setLoading] = useState(false);
    const [selectedIds, setSelectedIds] = useState<number[]>([]);

    const service = useMemo(() => new FaturaService(), []);
    const propostaService = useMemo(() => new PropostaService(), []);
    const comissaoService = useMemo(() => new ComissaoService(), []);
    const promotoraService = useMemo(() => new PromotoraService(), []);
    const regionalService = useMemo(() => new RegionalService(), []);
    const comercialService = useMemo(() => new ComercialService(), []);

    const [filtros, setFiltros] = useState({
        dataInicio: new Date().toISOString().split('T')[0],
        dataFim: new Date().toISOString().split('T')[0],
        status: '',
        termoBusca: ''
    });

    const [listaFaturas, setListaFaturas] = useState<FaturaResponseDTO[]>([]);
    const [faturaSelecionada, setFaturaSelecionada] = useState<FaturaResponseDTO | null>(null);

    const [showModalDetalhes, setShowModalDetalhes] = useState(false);
    const [showModalAcao, setShowModalAcao] = useState(false);
    const [showModalNovaFatura, setShowModalNovaFatura] = useState(false);
    const [showModalHistorico, setShowModalHistorico] = useState(false);
    const [showModalBancoCnab, setShowModalBancoCnab] = useState(false);

    const [obsAcao, setObsAcao] = useState('');
    const [tipoAcao, setTipoAcao] = useState<string>('');
    const [listaHistorico, setListaHistorico] = useState<HistoricoFaturaDTO[]>([]);

    const [listaContasBancarias, setListaContasBancarias] = useState<ContaBancariaDTO[]>([]);
    const [contaSelecionadaId, setContaSelecionadaId] = useState<string>('');

    const [formNovaFatura, setFormNovaFatura] = useState({
        inicio: new Date().toISOString().split('T')[0],
        fim: new Date().toISOString().split('T')[0],
        tipoOrigem: 'PROMOTORA',
        idOrigem: ''
    });
    const [listaOrigensNova, setListaOrigensNova] = useState<{ id: number, nome: string }[]>([]);
    const [loadingOrigens, setLoadingOrigens] = useState(false);

    useEffect(() => {
        buscarFaturas();
    }, []);

    useEffect(() => {
        if (showModalNovaFatura) {
            carregarOrigens(formNovaFatura.tipoOrigem);
        }
    }, [showModalNovaFatura, formNovaFatura.tipoOrigem]);

    const buscarFaturas = async () => {
        try {
            setLoading(true);
            setAlert(null);
            const response = await service.listarFaturas(filtros);
            const dados = Array.isArray(response.data) ? response.data : (response.data.content || []);
            setListaFaturas(dados);
            setLoading(false);
            setSelectedIds([]);
        } catch (err: any) {
            setLoading(false);
            setAlert({ message: 'Erro ao buscar faturas.', type: 'error' });
        }
    };

    const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.checked) {
            const allIds = listaFaturas.map(f => f.id);
            setSelectedIds(allIds);
        } else {
            setSelectedIds([]);
        }
    };

    const handleSelectOne = (id: number) => {
        if (selectedIds.includes(id)) {
            setSelectedIds(selectedIds.filter(i => i !== id));
        } else {
            setSelectedIds([...selectedIds, id]);
        }
    };

    const handleAprovacaoLote = async () => {
        const idsAprovaveis = listaFaturas
            .filter(f => selectedIds.includes(f.id) && (f.status === 'GERADA' || f.status === 'EM_ANALISE'))
            .map(f => f.id);

        if (idsAprovaveis.length === 0) {
            return setAlert({ message: 'Nenhuma fatura selecionada está elegível para aprovação (Status deve ser Gerada ou Em Análise).', type: 'warning' });
        }

        if (!window.confirm(`Deseja aprovar ${idsAprovaveis.length} faturas selecionadas?`)) return;

        setLoading(true);
        try {
            for (const id of idsAprovaveis) {
                await service.atualizarStatus(id, 'APROVADA' as any, 'Aprovação em lote via sistema');
            }

            setAlert({ message: `${idsAprovaveis.length} faturas foram aprovadas com sucesso!`, type: 'success' });
            buscarFaturas();
        } catch (err) {
            setAlert({ message: 'Erro ao realizar aprovação em lote.', type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleClickBotaoCnab = async () => {
        if (selectedIds.length === 0) {
            return setAlert({ message: 'Selecione ao menos uma fatura aprovada para gerar o arquivo CNAB.', type: 'warning' });
        }

        const faturasSelecionadasDetalhadas = listaFaturas.filter(f => selectedIds.includes(f.id));
        const naoAprovadas = faturasSelecionadasDetalhadas.some(f => f.status !== 'APROVADA' && f.status !== 'APROVADO');

        if (naoAprovadas) {
            return setAlert({ message: 'Existem faturas selecionadas que não estão APROVADAS. O CNAB só pode ser gerado para faturas com status Aprovada.', type: 'error' });
        }

        setLoading(true);
        try {
            const resp = await comissaoService.buscarConfigCnab();
            const contas = Array.isArray(resp.data) ? resp.data : (resp.data ? [resp.data] : []);

            if (contas.length === 0) {
                setAlert({ message: 'Nenhuma conta bancária configurada no sistema. Vá em Configurações > Financeiro.', type: 'error' });
                setLoading(false);
                return;
            }

            setListaContasBancarias(contas);

            const principal = contas.find((c: any) => c.principal);
            setContaSelecionadaId(principal ? principal.id.toString() : contas[0].id.toString());

            setLoading(false);
            setShowModalBancoCnab(true);

        } catch (err) {
            setLoading(false);
            setAlert({ message: 'Erro ao buscar contas bancárias.', type: 'error' });
        }
    };

    const confirmarGeracaoCnab = async () => {
        if (!contaSelecionadaId) return;

        setShowModalBancoCnab(false);
        setLoading(true);
        try {
            const response = await comissaoService.gerarRemessaCnab(selectedIds, parseInt(contaSelecionadaId));

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `remessa_cnab_${new Date().getTime()}.rem`);
            document.body.appendChild(link);
            link.click();
            link.remove();

            setAlert({ message: 'Arquivo de remessa gerado com sucesso!', type: 'success' });
            setSelectedIds([]);
        } catch (err) {
            console.error(err);
            setAlert({ message: 'Erro ao gerar remessa CNAB.', type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleAbrirHistorico = async (fatura?: FaturaResponseDTO) => {
        const alvo = fatura || faturaSelecionada;
        if (!alvo) return;
        setLoading(true);
        try {
            const response = await service.axios.get(`/${alvo.id}/historico`);
            setListaHistorico(response.data);
            setFaturaSelecionada(alvo);
            setShowModalHistorico(true);
            setShowModalDetalhes(false);
        } catch (error) {
            setAlert({ message: 'Erro ao carregar histórico.', type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const removerItemCalculo = async (idCalculo: number) => {
        if (!faturaSelecionada) return;
        if (!window.confirm("Confirma a remoção deste item? Esta ação será registrada no histórico e o valor da fatura será recalculado.")) return;
        setLoading(true);
        try {
            await service.axios.delete(`/${faturaSelecionada.id}/itens/${idCalculo}`);
            setAlert({ message: 'Item removido com sucesso.', type: 'success' });
            setShowModalDetalhes(false);
            buscarFaturas();
        } catch (error) {
            console.error(error);
            setAlert({ message: 'Erro ao remover item.', type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const carregarOrigens = async (tipo: string) => {
        setLoadingOrigens(true);
        setListaOrigensNova([]);
        try {
            let response;
            let dadosFormatados: { id: number, nome: string }[] = [];
            if (tipo === 'PROMOTORA') response = await promotoraService.listarTodasPromotorasAtivas();
            else if (tipo === 'REGIONAL') response = await regionalService.listarTodasRegionaisAtivas();
            else if (tipo === 'COMERCIAL') response = await comercialService.listarTodasComerciaisAtivas();
            const dadosRaw = response?.data || response || [];
            if (Array.isArray(dadosRaw)) {
                dadosFormatados = dadosRaw.map((item: any) => ({
                    id: item.id,
                    nome: item.nome || item.descricao || item.razaoSocial || `Item ${item.id}`
                }));
            }
            setListaOrigensNova(dadosFormatados);
        } catch (e) {
            setAlert({ message: 'Erro ao carregar lista de parceiros.', type: 'error' });
        } finally {
            setLoadingOrigens(false);
        }
    };

    const handleGerarFatura = async () => {
        if (!formNovaFatura.idOrigem || !formNovaFatura.inicio || !formNovaFatura.fim) {
            setAlert({ message: 'Preencha todos os campos.', type: 'warning' });
            return;
        }
        try {
            setLoading(true);
            await comissaoService.gerarFaturaIndividual(
                formNovaFatura.inicio,
                formNovaFatura.fim,
                parseInt(formNovaFatura.idOrigem),
                formNovaFatura.tipoOrigem
            );
            setAlert({ message: 'Fatura gerada!', type: 'success' });
            setShowModalNovaFatura(false);
            buscarFaturas();
        } catch (err) {
            setAlert({ message: 'Erro ao gerar fatura.', type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleBaixarArquivo = async (id: number, tipo: 'PDF' | 'EXCEL') => {
        try {
            setLoading(true);
            await service.baixarArquivo(id, tipo);
            setLoading(false);
            setAlert({ message: `Download iniciado.`, type: 'success' });
        } catch (err) {
            setLoading(false);
            setAlert({ message: `Erro ao gerar arquivo.`, type: 'error' });
        }
    };

    const abrirModalDetalhes = (fatura: FaturaResponseDTO) => {
        setFaturaSelecionada(fatura);
        setShowModalDetalhes(true);
    };

    const abrirModalAcao = (fatura: FaturaResponseDTO, acao: string) => {
        setFaturaSelecionada(fatura);
        setTipoAcao(acao);
        setObsAcao('');
        setShowModalAcao(true);
    };

    const confirmarAcao = async () => {
        if (!faturaSelecionada || !tipoAcao) return;
        try {
            setLoading(true);
            let statusEnvio = tipoAcao;
            if (tipoAcao === 'APROVADO') statusEnvio = 'APROVADA';
            else if (tipoAcao === 'REPROVADO') statusEnvio = 'REPROVADA';
            else if (tipoAcao === 'PAGO') statusEnvio = 'PAGA';
            else if (tipoAcao === 'PENDENTE_DOC') statusEnvio = 'PENDENTE';
            else if (tipoAcao === 'CANCELADO') statusEnvio = 'CANCELADA';

            await service.atualizarStatus(faturaSelecionada.id, statusEnvio as any, obsAcao);
            setAlert({ message: `Status atualizado!`, type: 'success' });
            setShowModalAcao(false);
            buscarFaturas();
        } catch (err) {
            setLoading(false);
            setAlert({ message: 'Erro ao atualizar status.', type: 'error' });
        }
    };

    const formatMoney = (val: number | undefined) => {
        if (val === undefined || val === null) return 'R$ 0,00';
        return val.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
    };

    const getBadgeStatus = (status: string) => {
        const s = status ? status.toUpperCase() : '';
        switch (s) {
            case 'APROVADO': case 'APROVADA': return <span className="badge bg-success">Aprovada</span>;
            case 'PAGO': case 'PAGA': return <span className="badge bg-primary">Paga</span>;
            case 'REPROVADO': case 'REPROVADA': return <span className="badge bg-danger">Reprovada</span>;
            case 'PENDENTE_DOC': case 'PENDENTE': return <span className="badge bg-warning text-dark">Pendente</span>;
            case 'GERADA': return <span className="badge bg-info text-dark">Gerada</span>;
            case 'EM_ANALISE': return <span className="badge bg-secondary">Em Análise</span>;
            case 'CANCELADA': return <span className="badge bg-dark">Cancelada</span>;
            default: return <span className="badge bg-light text-dark border">{s}</span>;
        }
    };

    const todasSelecionadas = listaFaturas.length > 0 && selectedIds.length === listaFaturas.length;

    return (
        <>
            {alert && (<CustomAlert message={alert.message} type={alert.type} onClose={() => setAlert(null)} />)}

            <div className="position-relative m-md-3">
                {loading && <Spinner loading={loading} />}

                <div className="card shadow-sm" style={{ marginTop: '25px' }}>
                    <div className="card-body">

                        <div className="d-flex justify-content-between align-items-center mb-4">
                            <h5 className="mb-0">Gestão de Extratos e Pagamentos</h5>
                            <div className="d-flex gap-2">
                                {selectedIds.length > 0 && (
                                    <button className="btn btn-outline-success animate-fade-in" onClick={handleAprovacaoLote}>
                                        <FontAwesomeIcon icon={faCheckDouble} className="me-2" />
                                        Aprovar Selecionadas
                                    </button>
                                )}

                                <button className="btn btn-warning text-dark" onClick={handleClickBotaoCnab}>
                                    <FontAwesomeIcon icon={faFileCode} className="me-2" />
                                    Gerar CNAB 240 {selectedIds.length > 0 ? `(${selectedIds.length})` : ''}
                                </button>

                                <button className="btn btn-success" onClick={() => setShowModalNovaFatura(true)}>
                                    <FontAwesomeIcon icon={faPlus} className="me-2" /> Nova Fatura Individual
                                </button>
                            </div>
                        </div>

                        <div className="row bg-light p-3 rounded mb-3 border g-3">
                            <div className="col-md-3">
                                <label className="form-label small fw-bold">Período</label>
                                <div className="input-group input-group-sm">
                                    <input type="date" className="form-control" value={filtros.dataInicio} onChange={e => setFiltros({ ...filtros, dataInicio: e.target.value })} />
                                    <input type="date" className="form-control" value={filtros.dataFim} onChange={e => setFiltros({ ...filtros, dataFim: e.target.value })} />
                                </div>
                            </div>
                            <div className="col-md-3">
                                <label className="form-label small fw-bold">Status</label>
                                <select className="form-select form-select-sm" value={filtros.status} onChange={e => setFiltros({ ...filtros, status: e.target.value })}>
                                    <option value="">Todos</option>
                                    <option value="GERADA">Gerada</option>
                                    <option value="EM_ANALISE">Em Análise</option>
                                    <option value="APROVADA">Aprovada</option>
                                    <option value="PAGA">Paga</option>
                                    <option value="PENDENTE">Pendente</option>
                                    <option value="REPROVADA">Reprovada</option>
                                </select>
                            </div>
                            <div className="col-md-4">
                                <label className="form-label small fw-bold">Buscar</label>
                                <div className="input-group input-group-sm">
                                    <input type="text" className="form-control" placeholder="Parceiro ou ID..." value={filtros.termoBusca} onChange={e => setFiltros({ ...filtros, termoBusca: e.target.value })} />
                                    <button className="btn btn-primary" onClick={buscarFaturas}>
                                        <FontAwesomeIcon icon={faSearch} /> Buscar
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div className="table-responsive">
                            <table className="table table-hover table-bordered align-middle">
                                <thead className="table-dark small">
                                    <tr>
                                        <th className="text-center" style={{ width: '3%' }}>
                                            <input
                                                type="checkbox"
                                                className="form-check-input"
                                                checked={todasSelecionadas}
                                                onChange={handleSelectAll}
                                                disabled={listaFaturas.length === 0}
                                            />
                                        </th>
                                        <th className="text-center" style={{ width: '5%' }}>ID</th>
                                        <th>Parceiro</th>
                                        <th className="text-center" style={{ width: '10%' }}>Criação</th>
                                        <th className="text-center" style={{ width: '8%' }}>Itens</th>
                                        <th className="text-end" style={{ width: '12%' }}>Total a Pagar</th>
                                        <th className="text-center" style={{ width: '10%' }}>Status</th>
                                        <th className="text-center" style={{ width: '5%' }}>Restrição</th>
                                        <th className="text-center" style={{ width: '15%' }}>Ações</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {listaFaturas.length > 0 ? (
                                        listaFaturas.map(fatura => (
                                            <tr key={fatura.id} className={selectedIds.includes(fatura.id) ? 'table-warning' : ''}>
                                                <td className="text-center">
                                                    <input
                                                        type="checkbox"
                                                        className="form-check-input"
                                                        checked={selectedIds.includes(fatura.id)}
                                                        onChange={() => handleSelectOne(fatura.id)}
                                                    />
                                                </td>
                                                <td className="text-center fw-bold text-muted">#{fatura.id}</td>
                                                <td>{fatura.nomeParceiro || '---'}</td>
                                                <td className="text-center">
                                                    {fatura.dataCriacao ? format(new Date(fatura.dataCriacao), 'dd/MM/yyyy') : '-'}
                                                </td>
                                                <td className="text-center">{fatura.itens?.length || 0}</td>
                                                <td className="text-end fw-bold text-success">{formatMoney(fatura.valorLiquido)}</td>
                                                <td className="text-center">{getBadgeStatus(fatura.status)}</td>
                                                <td className="text-center">
                                                    {fatura.possuiRestricaoParceiro && (
                                                        <FontAwesomeIcon icon={faExclamationTriangle} className="text-danger" title={fatura.motivoRestricao || 'Restrição cadastral'} />
                                                    )}
                                                </td>
                                                <td className="text-center">
                                                    <div className="btn-group btn-group-sm">
                                                        <button className="btn btn-outline-secondary" title="Detalhes" onClick={() => abrirModalDetalhes(fatura)}>
                                                            <FontAwesomeIcon icon={faEdit} />
                                                        </button>

                                                        <button className="btn btn-outline-info" title="Histórico" onClick={() => handleAbrirHistorico(fatura)}>
                                                            <FontAwesomeIcon icon={faHistory} />
                                                        </button>

                                                        {(fatura.status === 'EM_ANALISE' || fatura.status === 'GERADA') && (
                                                            <button className="btn btn-outline-success" title="Aprovar" onClick={() => abrirModalAcao(fatura, 'APROVADO')}>
                                                                <FontAwesomeIcon icon={faCheck} />
                                                            </button>
                                                        )}
                                                        {(fatura.status === 'APROVADA' || fatura.status === 'APROVADO') && (
                                                            <>
                                                                <button className="btn btn-outline-primary" title="Pagar" onClick={() => abrirModalAcao(fatura, 'PAGO')}>
                                                                    <FontAwesomeIcon icon={faMoneyBillWave} />
                                                                </button>
                                                                <button className="btn btn-outline-dark" title="PDF" onClick={() => handleBaixarArquivo(fatura.id, 'PDF')}>
                                                                    <FontAwesomeIcon icon={faFilePdf} />
                                                                </button>
                                                            </>
                                                        )}
                                                        {fatura.status !== 'CANCELADA' && fatura.status !== 'PAGA' && fatura.status !== 'PAGO' && (
                                                            <button className="btn btn-outline-warning" title="Pendência" onClick={() => abrirModalAcao(fatura, 'PENDENTE_DOC')}>
                                                                <FontAwesomeIcon icon={faTimes} />
                                                            </button>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr><td colSpan={9} className="text-center text-muted py-4">Nenhuma fatura encontrada.</td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>

                    </div>
                </div>

                {showModalBancoCnab && (
                    <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.6)' }} tabIndex={-1}>
                        <div className="modal-dialog modal-dialog-centered">
                            <div className="modal-content shadow">
                                <div className="modal-header bg-warning text-dark">
                                    <h5 className="modal-title fw-bold">
                                        <FontAwesomeIcon icon={faUniversity} className="me-2" />
                                        Selecionar Conta Bancária
                                    </h5>
                                    <button type="button" className="btn-close" onClick={() => setShowModalBancoCnab(false)}></button>
                                </div>
                                <div className="modal-body p-4">
                                    <div className="alert alert-secondary small mb-3">
                                        Você selecionou <strong>{selectedIds.length}</strong> faturas para gerar o arquivo de remessa.
                                        Escolha abaixo qual conta bancária será usada no cabeçalho do arquivo.
                                    </div>

                                    <label className="form-label fw-bold">Conta Bancária</label>
                                    <select
                                        className="form-select form-select-lg"
                                        value={contaSelecionadaId}
                                        onChange={e => setContaSelecionadaId(e.target.value)}
                                    >
                                        {listaContasBancarias.map(conta => (
                                            <option key={conta.id} value={conta.id}>
                                                {conta.nomeIdentificacao} ({conta.bancoNome}) {conta.principal ? ' - [Principal]' : ''}
                                            </option>
                                        ))}
                                    </select>

                                    <div className="mt-3 text-muted small">
                                        <FontAwesomeIcon icon={faCheck} className="me-1" />
                                        O arquivo será gerado com os dados da empresa pagadora vinculados a esta conta.
                                    </div>
                                </div>
                                <div className="modal-footer border-0 pt-0">
                                    <button type="button" className="btn btn-link text-decoration-none text-muted" onClick={() => setShowModalBancoCnab(false)}>Cancelar</button>
                                    <button type="button" className="btn btn-warning fw-bold px-4" onClick={confirmarGeracaoCnab}>
                                        <FontAwesomeIcon icon={faFileCode} className="me-2" /> Gerar Arquivo
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {showModalDetalhes && faturaSelecionada && (
                    <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }} tabIndex={-1}>
                        <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
                            <div className="modal-content">
                                <div className="modal-header bg-dark text-white">
                                    <h5 className="modal-title">Fatura #{faturaSelecionada.id}</h5>
                                    <button type="button" className="btn-close btn-close-white" onClick={() => setShowModalDetalhes(false)}></button>
                                </div>
                                <div className="modal-body bg-light">
                                    <div className="card border-0 shadow-sm mb-3">
                                        <div className="card-body">
                                            <h6 className="card-title text-primary">Itens da Fatura</h6>
                                            <div className="table-responsive">
                                                <table className="table table-sm table-hover">
                                                    <thead><tr><th>Contrato</th><th>Produto</th><th className="text-end">Valor</th><th>Ações</th></tr></thead>
                                                    <tbody>
                                                        {faturaSelecionada.itens?.map((item: any) => (
                                                            <tr key={item.id}>
                                                                <td>{item.contrato?.numeroContrato}</td>
                                                                <td>{item.parametroUtilizado?.comissaoPerfil?.nome}</td>
                                                                <td className="text-end">{formatMoney(item.valorComissao)}</td>
                                                                <td className="d-flex align-items-center">
                                                                    <button className="btn btn-sm btn-link text-danger" onClick={() => removerItemCalculo(item.id)}>
                                                                        <FontAwesomeIcon icon={faTrash} />
                                                                    </button>
                                                                    <Link to={`/proposta-esteira-component`} state={{ idProposta: item.contrato.id, esteira: 'N' }}>
                                                                        <Button className="btn btn-sm" style={{ background: 'grey', border: 'grey', marginLeft: '5px' }} title='Proposta'>
                                                                            <FontAwesomeIcon size="2xs" icon={faEye} />
                                                                        </Button>
                                                                    </Link>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="modal-footer bg-light justify-content-between">
                                    <div className="d-flex gap-2">
                                        <button className="btn btn-outline-danger" onClick={() => handleBaixarArquivo(faturaSelecionada!.id, 'PDF')}><FontAwesomeIcon icon={faFilePdf} /> PDF</button>
                                    </div>
                                    <button type="button" className="btn btn-secondary" onClick={() => setShowModalDetalhes(false)}>Fechar</button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {showModalNovaFatura && (
                    <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.6)' }} tabIndex={-1}>
                        <div className="modal-dialog modal-dialog-centered">
                            <div className="modal-content shadow">
                                <div className="modal-header bg-success text-white">
                                    <h5 className="modal-title fw-bold">Gerar Nova Fatura</h5>
                                    <button type="button" className="btn-close btn-close-white" onClick={() => setShowModalNovaFatura(false)}></button>
                                </div>
                                <div className="modal-body p-4">
                                    <div className="row g-3">
                                        <div className="col-md-6">
                                            <label>Início</label>
                                            <input type="date" className="form-control" value={formNovaFatura.inicio} onChange={e => setFormNovaFatura({ ...formNovaFatura, inicio: e.target.value })} />
                                        </div>
                                        <div className="col-md-6">
                                            <label>Fim</label>
                                            <input type="date" className="form-control" value={formNovaFatura.fim} onChange={e => setFormNovaFatura({ ...formNovaFatura, fim: e.target.value })} />
                                        </div>
                                        <div className="col-md-12">
                                            <label>Tipo Origem</label>
                                            <select className="form-select" value={formNovaFatura.tipoOrigem} onChange={e => setFormNovaFatura({ ...formNovaFatura, tipoOrigem: e.target.value, idOrigem: '' })}>
                                                <option value="PROMOTORA">Promotora</option>
                                                <option value="REGIONAL">Regional</option>
                                                <option value="COMERCIAL">Comercial</option>
                                            </select>
                                        </div>
                                        <div className="col-md-12">
                                            <label>Parceiro</label>
                                            <select className="form-select" value={formNovaFatura.idOrigem} onChange={e => setFormNovaFatura({ ...formNovaFatura, idOrigem: e.target.value })} disabled={loadingOrigens}>
                                                <option value="">Selecione...</option>
                                                {listaOrigensNova.map(o => <option key={o.id} value={o.id}>{o.nome}</option>)}
                                            </select>
                                        </div>
                                    </div>
                                </div>
                                <div className="modal-footer">
                                    <button type="button" className="btn btn-success" onClick={handleGerarFatura} disabled={loading}>Gerar</button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {showModalAcao && faturaSelecionada && (
                    <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.6)' }} tabIndex={-1}>
                        <div className="modal-dialog modal-dialog-centered">
                            <div className="modal-content shadow">
                                <div className="modal-header bg-dark text-white">
                                    <h5 className="modal-title">Alterar Status</h5>
                                    <button type="button" className="btn-close btn-close-white" onClick={() => setShowModalAcao(false)}></button>
                                </div>
                                <div className="modal-body p-4">
                                    <p>Confirmar alteração para <strong>{tipoAcao}</strong>?</p>
                                    <textarea className="form-control" placeholder="Observação..." value={obsAcao} onChange={e => setObsAcao(e.target.value)}></textarea>
                                </div>
                                <div className="modal-footer">
                                    <button type="button" className="btn btn-dark" onClick={confirmarAcao}>Confirmar</button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {showModalHistorico && (
                    <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
                        <div className="modal-dialog modal-lg"><div className="modal-content">
                            <div className="modal-header bg-secondary text-white"><h5 className="modal-title">Histórico</h5><button className="btn-close" onClick={() => setShowModalHistorico(false)}></button></div>
                            <div className="modal-body">
                                <table className="table table-striped">
                                    <thead><tr><th>Data</th><th>Desc</th><th>User</th></tr></thead>
                                    <tbody>{listaHistorico.map((h, i) => <tr key={i}><td>{h.dataEvento}</td><td>{h.descricao}</td><td>{h.usuario}</td></tr>)}</tbody>
                                </table>
                            </div>
                        </div></div>
                    </div>
                )}

            </div>
        </>
    );
}

export default FaturaGestaoComponent;